// What's new: what happened on the board in a period people pick (the last working day by default,
// so on a Monday it covers Friday and the weekend). The meeting brief's sections, in order: verified
// & closed, deployed, developed, new, updated, stuck; under each, the people (or areas, or projects)
// and their rows. A row opens its item in a panel beside the page, joined by the bridge. Read-only:
// viewers see it too. Copy summary puts the same view on the clipboard as text for Teams.

import {
  BRIEF_SECTIONS,
  briefCounts,
  byRelease,
  countsLine,
  leadStep,
  MAX_DAYS,
  PERIOD_PRESETS,
  presetOf,
  presetPeriod,
  rangeLabel,
  sectionLines,
  type BriefLine,
  type BriefNote,
  type BriefSection,
  type News,
  type PeriodPreset,
} from "@boem/core/brief";
import { nameList, projectCode, type Board, type BoardItem, type Viewer } from "@boem/core/shared";
import { dayLabel, dayTimeLabel, localDate, startOfDay } from "@boem/core/time";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api.ts";
import type { PanelTarget } from "../panels.ts";
import "../whatsnew.css";
import { newsQuery, shownBrief, summaryText, type NewsView } from "../whatsnew.ts";
import { AssigneeFilter } from "./AssigneeFilter.tsx";
import { RowAvatar } from "./Assign.tsx";
import { Icon } from "./Icons.tsx";
import { GoToToolbarButton } from "./QuickGoTo.tsx";
import { Lightbox } from "./Lightbox.tsx";
import { MenuButton } from "./LogView.tsx";
import { ShotStrip } from "./Media.tsx";
import { KindBadge } from "./Structure.tsx";

/** Only screenshots the credential scan cleared (or imported ones) show here. */
const CLEARED = new Set(["clean", "unscanned"]);

/** "Verified by Jake · Oct 5" (with "· 2 more" when more happened); a stuck row says what it waits for. */
export function rowHeadline(line: BriefLine, section: BriefSection, timeZone: string): string {
  if (section === "stuck") return line.text;
  const lead = leadStep(line);
  if (!lead) return line.text;
  const who = lead.who ? ` by ${lead.who}${lead.ai ? ` (AI · ${lead.ai})` : ""}` : "";
  const more = line.steps.length > 1 ? ` · ${line.steps.length - 1} more` : "";
  return `${lead.head}${who} · ${dayLabel(new Date(lead.at), timeZone)}${more}`;
}

/** The project's name in the filter and the copy: "01 Checkout". */
const projectName = (p: Pick<BoardItem, "num" | "title" | "projectNo">) => `${p.projectNo != null ? projectCode(p.projectNo) : `#${p.num}`} ${p.title}`;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // an older browser, or the page without clipboard permission: a hidden textarea
    try {
      const t = document.createElement("textarea");
      t.value = text;
      t.setAttribute("readonly", "");
      t.style.position = "fixed";
      t.style.opacity = "0";
      document.body.appendChild(t);
      t.select();
      const ok = document.execCommand("copy");
      t.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

function Row({
  line,
  section,
  note,
  item,
  shotId,
  timeZone,
  viewer,
  onOpen,
  onShot,
}: {
  line: BriefLine;
  section: BriefSection;
  note: BriefNote | undefined;
  /** the item as the board has it (for its screenshot); undefined until the archive is in */
  item: BoardItem | undefined;
  shotId: string | null;
  timeZone: string;
  viewer: Viewer;
  onOpen: () => void;
  onShot: (item: BoardItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const shot = item && shotId ? item.shots.find((s) => s.id === shotId && CLEARED.has(s.scan)) : undefined;
  const shotItem = item && shot ? { ...item, shots: [shot] } : null;
  const steps = line.steps;
  return (
    <>
      <div
        className="wn-row"
        data-section={section}
        data-num={line.num}
        data-open={open ? "1" : undefined}
        onClick={(e) => {
          // the row opens its item, except where a control inside was clicked
          if ((e.target as HTMLElement).closest("button, a, input, select, .shots, .wn-more")) return;
          onOpen();
        }}
      >
        <div className="wn-main">
          <button
            type="button"
            className="sub-act wn-tog"
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "Show"} what happened to ${line.no} and its latest note`}
            title={open ? "Hide" : "What happened, and the latest note"}
            onClick={() => setOpen(!open)}
          >
            <Icon name="chevron" />
          </button>
          <span className="wn-no">{line.no}</span>
          <KindBadge kind={line.kind} />
          <button type="button" className="xref wn-title" title={`Open it in a panel: ${line.title}`} onClick={onOpen}>
            {line.title}
          </button>
          {line.assignee && (
            <RowAvatar
              name={line.assignee}
              tip={`With ${nameList(line.assignees ?? [line.assignee])}`}
              me={line.assignee === viewer.name}
              people={(line.assignees ?? [line.assignee]).map((name) => ({ name }))}
            />
          )}
          <button type="button" className="sub-act wn-panel" aria-label={`Open ${line.no} in a panel`} title="Open in a panel to the right" onClick={onOpen}>
            <Icon name="panel" />
          </button>
        </div>
        <div className="wn-what">{rowHeadline(line, section, timeZone)}</div>
        {shotItem && (
          <div className="wn-shot">
            <ShotStrip item={shotItem} viewer={viewer} onOpen={() => onShot(shotItem)} onRemove={() => {}} onRelease={() => {}} />
          </div>
        )}
        {open && (
          <div className="wn-more">
            {steps.length > 0 && (
              <ol className="wn-steps" aria-label="What happened in this period">
                {steps.map((s, i) => (
                  <li key={i}>
                    <span className="wn-when">{dayTimeLabel(new Date(s.at), timeZone)}</span> {s.who ?? "Someone"}
                    {s.ai ? ` (AI · ${s.ai})` : ""} {s.text}
                  </li>
                ))}
              </ol>
            )}
            {note ? (
              <div className="wn-note">
                <div className="wn-note-h">
                  Latest note &middot; {note.who}
                  {note.ai ? ` (AI · ${note.ai})` : ""} &middot; {dayTimeLabel(new Date(note.at), timeZone)}
                </div>
                <div className="wn-note-b">{note.body}</div>
              </div>
            ) : (
              <div className="wn-note wn-none">No notes in this period.</div>
            )}
          </div>
        )}
      </div>
      {/* marks the row for the bridge and "Scroll to it", like a board's cards */}
      <i hidden data-card={line.num} />
    </>
  );
}

export function WhatsNew({
  board,
  view,
  onView,
  byNum,
  onPanel,
  onShown,
  onReset,
  onMenu,
  menuOpen,
  offline,
  toast,
}: {
  board: Board;
  view: NewsView;
  /** change what the page shows (the URL follows); `period`: the person picked a period */
  onView: (v: NewsView, period?: boolean) => void;
  /** an item from the board or the archive (verified items are in the archive) */
  byNum: (num: number) => BoardItem | undefined;
  onPanel: (target: PanelTarget, source: { num: number }) => void;
  /** the items the page lists, so a panel's "From" line and bridge can find its row */
  onShown: (nums: Set<number>) => void;
  onReset?: () => void;
  onMenu: () => void;
  menuOpen: boolean;
  offline: boolean;
  toast: (msg: string) => void;
}) {
  const [news, setNews] = useState<News | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<BoardItem | null>(null);
  const seq = useRef(0);
  const q = newsQuery(view);

  // load it, and again whenever the board changes (live updates); an older answer never wins
  useEffect(() => {
    const n = ++seq.current;
    const t = setTimeout(
      () =>
        api.whatsNew(q).then(
          (r) => {
            if (n !== seq.current) return;
            setNews(r);
            setError(null);
          },
          (e) => n === seq.current && setError(e instanceof ApiError ? e.message : "Couldn't reach the server."),
        ),
      news ? 120 : 0,
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, board]);

  const projects = useMemo(
    () => board.items.filter((i) => i.kind === "project").sort((a, b) => (a.projectNo ?? Infinity) - (b.projectNo ?? Infinity) || a.num - b.num),
    [board],
  );
  const project = view.project != null ? (projects.find((p) => p.num === view.project) ?? null) : null;
  // (a new period keeps the last answer on screen until its own is in)
  const current = news;
  const shown = useMemo(() => (current ? shownBrief(current.brief, view.asg) : null), [current, view.asg]);
  const counts = useMemo(() => (shown ? briefCounts(shown) : null), [shown]);
  // every item the page lists, once each, with who has it: what the assignee filter counts
  const listed = useMemo(() => {
    const m = new Map<number, BriefLine>();
    for (const g of current?.brief.groups ?? []) for (const s of BRIEF_SECTIONS) for (const l of g[s.id]) if (!m.has(l.num)) m.set(l.num, l);
    return [...m.values()];
  }, [current]);
  const shotOf = useMemo(() => new Map((current?.brief.shots ?? []).map((s) => [s.num, s.id])), [current]);

  useEffect(() => {
    if (!shown) return;
    onShown(new Set(shown.groups.flatMap((g) => BRIEF_SECTIONS.flatMap((s) => g[s.id].map((l) => l.num)))));
  }, [shown, onShown]);

  const tz = current?.timeZone ?? "America/New_York";
  const preset: PeriodPreset | "custom" = presetOf(view.period) ?? "custom";
  const today = localDate(new Date(), tz);
  const earliest = localDate(startOfDay(new Date(), tz, MAX_DAYS), tz);
  const pick = (id: PeriodPreset) => {
    // a date starts a week back; the date box is right there to change it
    if (id === "date") return onView({ ...view, period: presetPeriod("date", view.period.kind === "date" ? view.period.date : localDate(startOfDay(new Date(), tz, 7), tz)) }, true);
    onView({ ...view, period: presetPeriod(id) }, true);
  };
  const range = current ? rangeLabel(new Date(current.from), new Date(current.to), current.timeZone) : "";
  const summary = current && counts ? [range, countsLine(counts) || "nothing happened"].join(" · ") : "";

  const copy = async () => {
    if (!current) return;
    const ok = await copyText(summaryText(current, view, { project: project ? projectName(project) : null }));
    toast(ok ? "Copied the summary. Paste it into Teams." : "Couldn't copy: the browser said no. Select the page text instead.");
  };

  const open = (l: BriefLine) => onPanel({ kind: l.kind === "project" ? "project" : "item", num: l.num }, { num: l.num });
  const daysOption = view.period.kind === "days" && !presetOf(view.period) ? view.period.days : null;

  return (
    <>
      <div className="c1-stick wn-stick">
        <header className="mast-c1">
          <div className="wrap row1">
            <MenuButton open={menuOpen} onClick={onMenu} />
            <span className="vb-brand">Buy.OEM</span>
            <h1>What&rsquo;s new</h1>
            <span className="row1-meta">
              {summary && (
                <span className="wn-sum" title={current?.label}>
                  {summary}
                </span>
              )}
              {offline && <span className="vb-offline">reconnecting&hellip;</span>}
            </span>
            <span className="acts">
              <button type="button" className="chip sm wn-copy" disabled={!current} title="Copy this view as text, ready to paste into Teams" onClick={copy}>
                Copy summary
              </button>
              {onReset && (
                <button type="button" className="chip sm reset-btn" title="Close every unpinned panel; back to the main board, its filters cleared" onClick={onReset}>
                  Reset
                </button>
              )}
            </span>
          </div>
        </header>
        <div className="onerow-wrap">
          <div className="wrap onerow wn-bar">
            <GoToToolbarButton />
            <select className="chip wn-period" aria-label="Period" value={preset} onChange={(e) => pick(e.target.value as PeriodPreset)}>
                {PERIOD_PRESETS.filter((p) => p.id !== "meeting" || current?.meeting || view.period.kind === "meeting").map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
                {daysOption != null && <option value="custom">Last {daysOption} days</option>}
            </select>
            {view.period.kind === "date" && (
              <input
                type="date"
                className="chip wn-date"
                aria-label="Since this date"
                min={earliest}
                max={today}
                value={view.period.kind === "date" ? view.period.date : ""}
                onChange={(e) => e.target.value && onView({ ...view, period: presetPeriod("date", e.target.value) }, true)}
              />
            )}
            <span className="wn-seg" role="group" aria-label="Group by">
              {(["person", "area", "project"] as const).map((by) => (
                <button key={by} type="button" className="chip" aria-pressed={view.by === by} onClick={() => onView({ ...view, by })}>
                  By {by}
                </button>
              ))}
            </span>
            <select
              className="chip wn-proj"
              aria-label="Project"
              title="The main board, or one project's work (a project with a board of its own shows only here)"
              value={view.project ?? ""}
              onChange={(e) => onView({ ...view, project: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">Main board</option>
              {projects.map((p) => (
                <option key={p.num} value={p.num}>
                  {projectName(p)}
                  {p.ownBoard ? " (own board)" : ""}
                </option>
              ))}
            </select>
            <AssigneeFilter items={listed.map((l) => ({ status: "open" as const, assignee: l.assignee ? { name: l.assignee } : null, assignees: (l.assignees ?? (l.assignee ? [l.assignee] : [])).map((name) => ({ name })) }))} members={board.members} viewer={board.viewer} value={view.asg} onChange={(asg) => onView({ ...view, asg })} all />
          </div>
        </div>
      </div>

      <main className="wrap wn">
        {current && <p className="wn-label">{current.label}{project ? ` · project ${projectName(project)}` : ""}</p>}
        {error && !current ? (
          <div className="empty">{error}</div>
        ) : !shown ? (
          <div className="empty">Loading what&rsquo;s new&hellip;</div>
        ) : (
          <>
            {error && <p className="wn-err" role="status">{error}</p>}
            {/* due dates (OEM-267): open items past their due date, whatever happened in the period */}
            {(shown.overdue?.length ?? 0) > 0 && (
              <section className="wn-sec" data-section="overdue" aria-label="Overdue">
                <h2 className="section-head wn-head" title="Open items past their due date, the most overdue first">
                  Overdue <span className="n">&middot; {shown.overdue!.length}</span>
                  <span className="wn-sub"> past their due date, still open</span>
                </h2>
                <div className="wn-group">
                  {shown.overdue!.map((l) => (
                    <Fragment key={l.num}>
                      <Row line={l} section="stuck" note={shown.notes[l.num]} item={byNum(l.num)} shotId={null} timeZone={tz} viewer={board.viewer} onOpen={() => open(l)} onShot={setLightbox} />
                    </Fragment>
                  ))}
                </div>
              </section>
            )}
            {!shown.groups.length && <div className="empty">Nothing happened on {project ? "this project" : "the board"} in this period.</div>}
            {shown.groups.length > 0 &&
              BRIEF_SECTIONS.map((s) => {
                const rows = sectionLines(shown, s.id);
                // In PR review is OEMUAP's (GitHub): a personal board has none
                if (s.id === "review" && board.org?.kind === "personal") return null;
                return (
                  <section key={s.id} className="wn-sec" data-section={s.id} aria-label={s.label}>
                    <h2 className="section-head wn-head" title={s.hint}>
                      {s.label} <span className="n">&middot; {counts?.[s.id] ?? 0}</span>
                      {s.id === "deployed" && <span className="wn-sub"> waiting for verify</span>}
                      {s.id === "developed" && <span className="wn-sub"> waiting for deploy</span>}
                      {s.id === "review" && <span className="wn-sub"> waiting for a reviewer or the merge</span>}
                      {s.id === "stuck" && <span className="wn-sub"> nothing happened in the period</span>}
                    </h2>
                    {!rows.length ? (
                      <p className="wn-empty">None in this period.</p>
                    ) : s.id === "deployed" && rows.some((r) => r.line.release) ? (
                      // deployed by GitHub in a release (docs/github.md): grouped by the release they went out in
                      // (one row per item: the line of whoever deployed it, which names the release)
                      byRelease(
                        [...new Map(rows.map((r) => [r.line.num, r])).keys()].map((num) => {
                          const r = rows.find((x) => x.line.num === num && x.line.release) ?? rows.find((x) => x.line.num === num)!;
                          return { ...r, release: r.line.release ?? null };
                        }),
                      ).map((g) => (
                        <div className="wn-group wn-release" key={g.release ?? ""}>
                          <div className="wn-gname">{g.release ? `Released in ${g.release}` : "Release not known"}</div>
                          {g.lines.map(({ line: l }) => (
                            <Fragment key={l.num}>
                              <Row
                                line={l}
                                section={s.id}
                                note={shown.notes[l.num]}
                                item={byNum(l.num)}
                                shotId={shotOf.get(l.num) ?? null}
                                timeZone={tz}
                                viewer={board.viewer}
                                onOpen={() => open(l)}
                                onShot={setLightbox}
                              />
                            </Fragment>
                          ))}
                        </div>
                      ))
                    ) : (
                      shown.groups
                        .filter((g) => g[s.id].length)
                        .map((g) => (
                          <div className="wn-group" key={g.name}>
                            <div className="wn-gname">{g.name}</div>
                            {g[s.id].map((l) => (
                              <Fragment key={l.num}>
                                <Row
                                  line={l}
                                  section={s.id}
                                  note={shown.notes[l.num]}
                                  item={byNum(l.num)}
                                  shotId={shotOf.get(l.num) ?? null}
                                  timeZone={tz}
                                  viewer={board.viewer}
                                  onOpen={() => open(l)}
                                  onShot={setLightbox}
                                />
                              </Fragment>
                            ))}
                          </div>
                        ))
                    )}
                  </section>
                );
              })}
          </>
        )}
      </main>
      {lightbox && <Lightbox item={lightbox} start={0} onClose={() => setLightbox(null)} />}
    </>
  );
}
